import {
  FIELD_DATABASE_NAME,
  FIELD_DATABASE_VERSION,
  type FieldDatabase,
  type FieldRecord,
  type FieldStoreName,
  getMissingFieldStores,
} from '@/shared/db/schema';

export class BrowserFieldDatabase implements FieldDatabase {
  private databasePromise?: Promise<IDBDatabase>;

  async get<K extends FieldStoreName>(storeName: K, id: string) {
    const store = await this.openObjectStore(storeName, 'readonly');
    const request = store.get(id);

    return requestToPromise<FieldRecord<K> | undefined>(request);
  }

  async getAll<K extends FieldStoreName>(storeName: K) {
    const store = await this.openObjectStore(storeName, 'readonly');
    const request = store.getAll();

    return requestToPromise<Array<FieldRecord<K>>>(request);
  }

  async put<K extends FieldStoreName>(storeName: K, record: FieldRecord<K>) {
    const store = await this.openObjectStore(storeName, 'readwrite');
    await requestToPromise(store.put(record));
  }

  async delete(storeName: FieldStoreName, id: string) {
    const store = await this.openObjectStore(storeName, 'readwrite');
    await requestToPromise(store.delete(id));
  }

  async clear(storeName: FieldStoreName) {
    const store = await this.openObjectStore(storeName, 'readwrite');
    await requestToPromise(store.clear());
  }

  async count(storeName: FieldStoreName) {
    const store = await this.openObjectStore(storeName, 'readonly');

    return requestToPromise<number>(store.count());
  }

  private async openObjectStore(storeName: FieldStoreName, mode: IDBTransactionMode) {
    const database = await this.open();
    const transaction = database.transaction(storeName, mode);

    return transaction.objectStore(storeName);
  }

  private open() {
    if (!globalThis.indexedDB) {
      return Promise.reject(new Error('IndexedDB is not available in this browser context.'));
    }

    this.databasePromise ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = globalThis.indexedDB.open(FIELD_DATABASE_NAME, FIELD_DATABASE_VERSION);

      request.onerror = () => reject(request.error ?? new Error('Unable to open inventory field database.'));
      request.onupgradeneeded = () => updateStores(request.result);
      request.onsuccess = () => resolve(request.result);
    });

    return this.databasePromise;
  }
}

function updateStores(database: IDBDatabase) {
  for (const storeName of getMissingFieldStores(database.objectStoreNames)) {
    database.createObjectStore(storeName, { keyPath: 'id' });
  }
}

function requestToPromise<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
    request.onsuccess = () => resolve(request.result);
  });
}
